import { GoToStatus, type RequisitionMatch } from './goToRequisition.ts'
import { branchesIn } from './syncRefusal.ts'

/**
 * The requisição in hand is not in the queue yet: ask the ERP for it (#501).
 *
 * Discovery reads forward from a watermark, so a requisição it has not
 * reached -- or has already passed -- is in the ERP and not here. The queue's
 * empty state answers that with "Buscar no ERP"; this is the same request,
 * made without a button because Enter on a whole number already said so.
 *
 * The server's own words are kept for a refusal: "não existe no ERP", "não
 * foi possível falar com o ERP" and "nenhum ERP configurado" ask different
 * things of whoever is holding the paper.
 */
export type SyncRequisition = (
  quotationNumber: number,
  branchCode?: number,
) => Promise<{ readonly verificationId: string; readonly branchCode?: number }>

export type Fetched =
  | { readonly status: typeof GoToStatus.Choosing; readonly matches: readonly RequisitionMatch[] }
  | { readonly status: typeof GoToStatus.NotFound }
  | { readonly status: typeof GoToStatus.Refused; readonly message: string }

export async function fetchRequisition(
  wanted: { readonly quotationNumber: number; readonly branchCode?: number | undefined },
  sync: SyncRequisition,
): Promise<Fetched> {
  try {
    const found = await sync(wanted.quotationNumber, wanted.branchCode)
    return {
      status: GoToStatus.Choosing,
      matches: [
        {
          // Zero only if the server named no filial; the id opens it then.
          branchCode: found.branchCode ?? wanted.branchCode ?? 0,
          quotationNumber: wanted.quotationNumber,
          patientName: '',
          verificationId: found.verificationId,
          inQueue: true,
        },
      ],
    }
  } catch (cause) {
    const several = branchesIn(cause)
    if (several.length > 1) {
      return {
        status: GoToStatus.Choosing,
        matches: several.map((branch) => ({
          branchCode: branch.code,
          branchName: branch.name,
          quotationNumber: wanted.quotationNumber,
          patientName: '',
          inQueue: false,
        })),
      }
    }
    const said = cause instanceof Error ? cause.message.trim() : ''
    return said === ''
      ? { status: GoToStatus.NotFound }
      : { status: GoToStatus.Refused, message: said }
  }
}
